import { Box, Loader, Text, Tooltip, useMantineColorScheme } from "@mantine/core";
import { type MouseEvent, useState } from "react";
import { useDisclaimer } from "src/contexts/DisclaimerContext";
import { getDisclaimerMessages } from "src/utils/referencesHandler";
import type { Reference } from "../db";
import { useApiStreamingState } from "../hooks/useChatCompletion";
import { onMiddleClick, useArtifactHandler } from "../utils/artifactHandler";
import { ImageReferenceCard, ImageViewerModal, isImageReference } from "./ImageReference";
import { ReferenceTooltip } from "./ReferenceViewer";

const TOOLTIP_STYLES = {
    tooltip: {
        pointerEvents: "auto" as const,
        backgroundColor: "transparent",
        padding: 0,
        boxShadow: "none",
        maxWidth: "350px",
        minWidth: "200px",
    },
};

interface InlineReferenceProps {
    reference: Reference;
    index: string;
    chatId?: string;
    showDisclaimer?: boolean;
}

export const InlineReference = ({
    reference,
    index,
    chatId,
    showDisclaimer = false,
}: InlineReferenceProps) => {
    const isStreaming = useApiStreamingState(chatId);
    const [isLoading, setIsLoading] = useState(false);
    const { handleArtifactClick, getFullUrl } = useArtifactHandler();
    const { openDisclaimer } = useDisclaimer();
    const { colorScheme } = useMantineColorScheme();
    const [imageViewerOpened, setImageViewerOpened] = useState(false);

    const handleClick = async (e: MouseEvent<HTMLAnchorElement>) => {
        if (isLoading) {
            e.preventDefault();
            return;
        }
        setIsLoading(true);
        try {
            await handleArtifactClick(
                e,
                reference.source,
                showDisclaimer ? getDisclaimerMessages(reference.dataSourceType) : [],
                openDisclaimer,
            );
        } finally {
            setIsLoading(false);
        }
    };

    if (isImageReference(reference)) {
        return (
            <>
                <Tooltip
                    disabled={isStreaming || imageViewerOpened}
                    closeDelay={600}
                    openDelay={100}
                    withArrow
                    position="top"
                    styles={TOOLTIP_STYLES}
                    label={
                        <ImageReferenceCard
                            reference={reference}
                            onOpen={() => setImageViewerOpened(true)}
                        />
                    }
                >
                    <Text
                        component="span"
                        role="button"
                        tabIndex={0}
                        data-testid={`${reference.reference_id}-inline-reference`}
                        onClick={() => setImageViewerOpened(true)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setImageViewerOpened(true);
                            }
                        }}
                        style={(theme) => ({
                            display: "inline-block",
                            backgroundColor:
                                colorScheme === "dark"
                                    ? theme.colors.dark[4]
                                    : theme.colors.gray[1],
                            padding: "2px 6px",
                            margin: "2px",
                            borderRadius: theme.radius.sm,
                            cursor: "pointer",
                            textDecoration: "none",
                            fontSize: "0.6rem",
                            color: "inherit",
                        })}
                    >
                        {index}
                    </Text>
                </Tooltip>
                <ImageViewerModal
                    reference={reference}
                    opened={imageViewerOpened}
                    onClose={() => setImageViewerOpened(false)}
                />
            </>
        );
    }

    return (
        <Tooltip
            disabled={isStreaming}
            closeDelay={600}
            openDelay={100}
            withArrow
            position="top"
            styles={TOOLTIP_STYLES}
            label={<ReferenceTooltip reference={reference} showDisclaimer={showDisclaimer} />}
        >
            {reference.source ? (
                <Text
                    component="a"
                    data-testid={`${reference.reference_id}-inline-reference`}
                    href={getFullUrl(reference.source)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleClick}
                    onAuxClick={onMiddleClick(handleClick)}
                    style={(theme) => ({
                        display: "inline-block",
                        backgroundColor:
                            colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[1],
                        padding: "2px 6px",
                        margin: "2px",
                        borderRadius: theme.radius.sm,
                        cursor: isLoading ? "default" : "pointer",
                        textDecoration: "none",
                        fontSize: "0.6rem",
                        color: "inherit",
                        position: "relative",
                        opacity: isLoading ? 0.7 : 1,
                        "&:hover": {
                            backgroundColor: !isLoading ? theme.colors.purple?.[1] : undefined,
                            color: !isLoading ? "#fff" : undefined,
                        },
                    })}
                >
                    {isLoading && (
                        <Box
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 1,
                            }}
                        >
                            <Loader size="xs" />
                        </Box>
                    )}
                    <span style={{ visibility: isLoading ? "hidden" : "visible" }}>{index}</span>
                </Text>
            ) : (
                <Text
                    component="span"
                    style={(theme) => ({
                        display: "inline-block",
                        backgroundColor:
                            colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[1],
                        padding: "2px 6px",
                        margin: "2px",
                        borderRadius: theme.radius.sm,
                        cursor: "pointer",
                        textDecoration: "none",
                        fontSize: "0.6rem",
                        color: "inherit",
                        "&:hover": { backgroundColor: theme.colors.purple?.[1], color: "#fff" },
                    })}
                >
                    {index}
                </Text>
            )}
        </Tooltip>
    );
};
